import React, { useState } from 'react';
import { Mafs, Coordinates, Plot, Line, Theme, Text } from 'mafs';
import { Card, Slider, Switch, Row, Col } from 'antd';

export const NormalDistributionWidget = ({ config = {}, variables = {} }) => {
    const [mu, setMu] = useState(variables.mu || config.mu || 0);
    const [sigma, setSigma] = useState(Math.max(0.1, variables.sigma || config.sigma || 1));
    const [showStdDevs, setShowStdDevs] = useState(true);

    const gaussian = (x) => {
        const exponent = -0.5 * Math.pow((x - mu) / sigma, 2);
        const denominator = sigma * Math.sqrt(2 * Math.PI);
        return (1 / denominator) * Math.exp(exponent);
    };

    const peak = gaussian(mu);
    const xMin = mu - 4.5 * sigma;
    const xMax = mu + 4.5 * sigma;

    // Expand Y-min to make room for labels below the axis
    const yMin = -peak * 0.15;
    const yMax = peak * 1.2;

    return (
        <div className="flexColumn gap-medium width-100">
            <div className="ch-emph-block" style={{ height: 350 }}>
                <Mafs
                    viewBox={{ x: [xMin, xMax], y: [yMin, yMax] }}
                    preserveAspectRatio={false}
                    pan={false}
                >
                    <Coordinates.Cartesian xAxis={{ lines: true }} yAxis={{ lines: false }} />

                    <Plot.OfX y={gaussian} color={Theme.red} weight={3} minSamplingDepth={12} />

                    {/* Mean Line */}
                    <Line.Segment point1={[mu, 0]} point2={[mu, peak]} style="dashed" color={Theme.gray} />
                    <Text x={mu} y={peak * 1.08} weight="bold">μ={mu}</Text>

                    {/* Sigma Markers */}
                    {showStdDevs && [-3, -2, -1, 1, 2, 3].map(sd => {
                        const x = mu + (sd * sigma);
                        const y = gaussian(x);
                        return (
                            <g key={sd}>
                                <Line.Segment point1={[x, 0]} point2={[x, y]} style="dotted" color={Theme.gray} opacity={0.5} />
                                <Text x={x} y={-peak * 0.08} size={12}>
                                    {sd > 0 ? '+' : ''}{sd}σ
                                </Text>
                            </g>
                        );
                    })}
                </Mafs>
            </div>

            <Card className="ch-card padding--m bg-white border-all--s">
                <Row gutter={[24, 16]} align="middle">
                    <Col span={10}>
                        <div className="flexSpaceBetween ch-text-small margin-b--xs">
                            <span className="ch-text-bold">Mean (μ)</span>
                            <span className="text-muted">{mu}</span>
                        </div>
                        <Slider min={-10} max={10} step={0.5} value={mu} onChange={(v) => setMu(v ?? 0)} />
                    </Col>
                    <Col span={10}>
                        <div className="flexSpaceBetween ch-text-small margin-b--xs">
                            <span className="ch-text-bold">Std Deviation (σ)</span>
                            <span className="text-muted">{sigma}</span>
                        </div>
                        <Slider min={0.5} max={5} step={0.1} value={sigma} onChange={(v) => setSigma(Math.max(0.1, v ?? 1))} />
                    </Col>
                    <Col span={4} className="flexAlignCentre justify-center">
                        <div className="flexColumn align-center gap-small">
                            <span className="ch-text-small text-muted">Show σ</span>
                            <Switch size="small" checked={showStdDevs} onChange={setShowStdDevs} />
                        </div>
                    </Col>
                </Row>
            </Card>
        </div>
    );
};